import React, { useState } from "react";
import jsPDF from "jspdf";

// ── Colores BioScope ──────────────────────────────────────────────────────
const C = {
  navy: "#1a2744",
  emerald: "#059669",
  slate: "#35627A",
  light: "#f8fafb",
  border: "#e2e5e4",
  text: "#1a1a1a",
  muted: "#6b7280",
  warn: "#D97706",
  danger: "#B46258",
};

// ── Helpers ───────────────────────────────────────────────────────────────
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function splitText(doc, text, maxWidth) {
  return doc.splitTextToSize(text || "", maxWidth);
}

function drawRect(doc, x, y, w, h, color, fill = true) {
  const [r, g, b] = hexToRgb(color);
  if (fill) {
    doc.setFillColor(r, g, b);
    doc.rect(x, y, w, h, "F");
  } else {
    doc.setDrawColor(r, g, b);
    doc.rect(x, y, w, h, "S");
  }
}

function setFont(doc, size, color = C.text, bold = false) {
  doc.setFontSize(size);
  const [r, g, b] = hexToRgb(color);
  doc.setTextColor(r, g, b);
  doc.setFont("helvetica", bold ? "bold" : "normal");
}

function writeText(doc, text, x, y, options = {}) {
  const { align = "left", maxWidth } = options;
  if (maxWidth) {
    const lines = splitText(doc, text, maxWidth);
    doc.text(lines, x, y, { align });
    return lines.length;
  }
  doc.text(text || "", x, y, { align });
  return 1;
}

// ── Main export function ──────────────────────────────────────────────────
export async function generarPDF({
  problem,
  sector,
  departamento,
  facturacion,
  plazo,
  objetivo,
  foda,
  horizontes,
  impacto,
  sostenibilidad,
  micmac,
  tam,
  sam,
  som,
  plan,
  signals,
  montecarlo,
}) {
  // Extract canvas from plan if available
  const canvasData = plan?.canvas?.canvas || [];
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  const margin = 18;
  const contentW = W - margin * 2;
  let y = 0;

  const fecha = new Date().toLocaleDateString("es-CO", {
    year: "numeric", month: "long", day: "numeric",
  });

  console.log("PDF datos:", {
    impacto: JSON.stringify(impacto)?.substring(0, 300),
    horizontes: JSON.stringify(horizontes)?.substring(0, 300),
    micmac_vars: micmac?.variables?.length,
    montecarlo_n: montecarlo?.length,
  });

  // ── PORTADA ───────────────────────────────────────────────────────────
  // Fondo navy
  drawRect(doc, 0, 0, W, H, C.navy);

  // Franja emerald superior
  drawRect(doc, 0, 0, W, 3, C.emerald);

  // Logo / Marca
  setFont(doc, 28, "#ffffff", true);
  doc.text("BioScope", margin, 45);
  setFont(doc, 10, C.emerald);
  doc.text("Inteligencia Estratégica para la Bioeconomía", margin, 53);

  // Línea divisora
  drawRect(doc, margin, 62, contentW, 0.4, "#ffffff");

  // Título del informe
  setFont(doc, 14, "#ffffff", true);
  const titleLines = splitText(doc, "Informe de Inteligencia Estratégica", contentW - 10);
  doc.text(titleLines, margin, 75);

  // Pregunta estratégica
  setFont(doc, 11, C.emerald);
  const qLines = splitText(doc, problem || "Análisis estratégico", contentW - 10);
  doc.text(qLines, margin, 88);

  // Metadatos
  const metaY = 140;
  const metas = [
    ["Sector", sector || "—"],
    ["Departamento", departamento || "—"],
    ["Tamaño empresa", facturacion || "—"],
    ["Plazo decisión", plazo || "—"],
    ["Objetivo", objetivo || "—"],
    ["Fecha", fecha],
  ];

  metas.forEach(([label, value], i) => {
    const row = metaY + i * 14;
    setFont(doc, 8, C.emerald);
    doc.text(label.toUpperCase(), margin, row);
    setFont(doc, 10, "#ffffff");
    doc.text(value, margin, row + 6);
    drawRect(doc, margin, row + 9, contentW, 0.2, "#334155");
  });

  // Footer portada
  setFont(doc, 8, "#64748b");
  doc.text("Confidencial — Generado por BioScope", margin, H - 12);
  doc.text("1", W - margin, H - 12, { align: "right" });

  // ── FUNCIÓN: nueva página ─────────────────────────────────────────────
  let pageNum = 1;

  function nuevaPagina(titulo = "") {
    doc.addPage();
    pageNum++;

    // Header
    drawRect(doc, 0, 0, W, 14, C.navy);
    setFont(doc, 7, "#94a3b8");
    doc.text("BioScope — Informe de Inteligencia Estratégica", margin, 9);
    setFont(doc, 7, C.emerald);
    doc.text(titulo, W - margin, 9, { align: "right" });

    // Footer
    drawRect(doc, 0, H - 10, W, 10, "#f1f5f9");
    setFont(doc, 7, C.muted);
    doc.text(fecha, margin, H - 4);
    doc.text(String(pageNum), W - margin, H - 4, { align: "right" });

    // Franja emerald superior
    drawRect(doc, 0, 0, W, 1.5, C.emerald);

    return 22; // y inicial
  }

  function sectionTitle(titulo, yPos) {
    drawRect(doc, margin, yPos, contentW, 8, "#f1f5f9");
    drawRect(doc, margin, yPos, 3, 8, C.emerald);
    setFont(doc, 10, C.navy, true);
    doc.text(titulo, margin + 6, yPos + 5.5);
    return yPos + 14;
  }

  function kpiBox(label, value, sub, x, boxY, w) {
    drawRect(doc, x, boxY, w - 2, 22, "#f8fafb");
    drawRect(doc, x, boxY, w - 2, 22, C.border, false);
    setFont(doc, 7, C.muted);
    doc.text(label, x + 4, boxY + 6);
    setFont(doc, 13, C.navy, true);
    doc.text(value || "—", x + 4, boxY + 14);
    if (sub) {
      setFont(doc, 7, C.muted);
      doc.text(sub, x + 4, boxY + 20);
    }
  }

  function checkOverflow(yPos, needed = 30) {
    if (yPos + needed > H - 15) {
      return nuevaPagina("continuación");
    }
    return yPos;
  }

  // ── PÁG 2: SEÑALES Y ESCENARIOS ───────────────────────────────────────
  y = nuevaPagina("Señales del entorno");

  // Resumen de señales
  y = sectionTitle("Señales del mercado recuperadas", y);
  const nSignals = signals?.length || 0;
  const nOpps = signals?.filter(s => s.classification === "Oportunidad").length || 0;
  const nRisks = signals?.filter(s => s.classification === "Riesgo").length || 0;
  const avgImpact = nSignals > 0
    ? Math.round(signals.reduce((a, s) => a + (+s.impact_probability || 0), 0) / nSignals * 100)
    : 0;

  const kpiW = contentW / 4;
  kpiBox("SEÑALES ANALIZADAS", String(nSignals), "fuentes diversas", margin, y, kpiW);
  kpiBox("OPORTUNIDADES", String(nOpps), `${Math.round(nOpps / Math.max(nSignals, 1) * 100)}% del total`, margin + kpiW, y, kpiW);
  kpiBox("RIESGOS", String(nRisks), `${Math.round(nRisks / Math.max(nSignals, 1) * 100)}% del total`, margin + kpiW * 2, y, kpiW);
  kpiBox("IMPACTO PROM.", `${avgImpact}%`, "probabilidad media", margin + kpiW * 3, y, kpiW);
  y += 28;

  // Top 5 señales
  y = sectionTitle("Señales más relevantes", y);
  const topSignals = (signals || [])
    .sort((a, b) => (+b.impact_probability || 0) - (+a.impact_probability || 0))
    .slice(0, 5);

  topSignals.forEach((s, i) => {
    y = checkOverflow(y, 14);
    const impact = Math.round((+s.impact_probability || 0) * 100);
    const impactColor = impact >= 70 ? C.emerald : impact >= 50 ? C.warn : C.muted;
    drawRect(doc, margin, y, contentW, 12, i % 2 === 0 ? "#f8fafb" : "#ffffff");
    setFont(doc, 8, C.text);
    const title = (s.Title || "").substring(0, 70);
    doc.text(title, margin + 3, y + 5);
    setFont(doc, 7, C.muted);
    doc.text(`${s.pestel_category || ""} · ${s.time_horizon || ""} · ${s.Source || ""}`, margin + 3, y + 10);
    setFont(doc, 8, impactColor, true);
    doc.text(`${impact}%`, W - margin - 3, y + 7, { align: "right" });
    y += 13;
  });

  // ── PÁG 3: ANÁLISIS ESTRATÉGICO ───────────────────────────────────────
  y = nuevaPagina("Análisis estratégico");

  // FODA
  if (foda) {
    y = sectionTitle("Análisis FODA", y);
    const quadrants = [
      { label: "Fortalezas", items: foda.fortalezas, color: C.slate },
      { label: "Oportunidades", items: foda.oportunidades, color: C.emerald },
      { label: "Debilidades", items: foda.debilidades, color: C.warn },
      { label: "Amenazas", items: foda.amenazas, color: C.danger },
    ];
    const qW = contentW / 2 - 3;
    const qH = 40;

    quadrants.forEach((q, i) => {
      const qX = margin + (i % 2) * (qW + 6);
      const qY = y + Math.floor(i / 2) * (qH + 4);
      drawRect(doc, qX, qY, qW, qH, "#f8fafb");
      drawRect(doc, qX, qY, qW, 6, q.color);
      setFont(doc, 7, "#ffffff", true);
      doc.text(q.label.toUpperCase(), qX + 3, qY + 4.5);
      setFont(doc, 7, C.text);
      (q.items || []).slice(0, 3).forEach((item, j) => {
        const txt = splitText(doc, `• ${item}`, qW - 6);
        doc.text(txt[0], qX + 3, qY + 11 + j * 9);
      });
    });
    y += qH * 2 + 12;
  }

  // Horizontes McKinsey - h1/h2/h3 son arrays de senales
  if (horizontes) {
    y = checkOverflow(y, 58);
    y = sectionTitle("Horizontes estrategicos McKinsey", y);
    const hData = [
      { key: "h1", label: "H1 - Corto plazo", color: C.danger },
      { key: "h2", label: "H2 - Mediano plazo", color: C.warn },
      { key: "h3", label: "H3 - Largo plazo", color: C.emerald },
    ];
    const hW = contentW / 3 - 2;

    hData.forEach((h, i) => {
      const sigs = Array.isArray(horizontes[h.key]) ? horizontes[h.key] : [];
      const opps = sigs.filter(s => s.classification === "Oportunidad").slice(0, 2);
      const risks = sigs.filter(s => s.classification === "Riesgo").slice(0, 1);
      const hX = margin + i * (hW + 3);
      drawRect(doc, hX, y, hW, 52, "#f8fafb");
      drawRect(doc, hX, y, hW, 6, h.color);
      setFont(doc, 7, "#ffffff", true);
      doc.text(h.label, hX + 2, y + 4.5);
      setFont(doc, 7, C.slate, true);
      doc.text(sigs.length + " senales", hX + 2, y + 12);
      setFont(doc, 6.5, C.emerald, true);
      doc.text("Oportunidades:", hX + 2, y + 18);
      setFont(doc, 6.5, C.text);
      opps.forEach((s, si) => {
        const t = splitText(doc, (s.Title || "").substring(0, 38), hW - 4);
        doc.text(t[0], hX + 2, y + 24 + si * 8);
      });
      if (risks.length > 0) {
        setFont(doc, 6.5, C.danger, true);
        doc.text("Riesgo:", hX + 2, y + 42);
        setFont(doc, 6.5, C.text);
        const t = splitText(doc, (risks[0].Title || "").substring(0, 38), hW - 4);
        doc.text(t[0], hX + 2, y + 48);
      }
    });
    y += 60;
  }

  // MICMAC - Análisis de impacto cruzado de variables
  if (micmac?.narrativa) {
    y = nuevaPagina("Análisis MICMAC");
    y = sectionTitle("Análisis de impacto cruzado de variables (MICMAC)", y);

    // Variables por cuadrante
    if (micmac.variables?.length > 0) {
      const cuadrantes = ["Poder", "Conflicto", "Resultado", "Autónoma"];
      const cColors = [C.navy, C.danger, C.warn, C.muted];

      cuadrantes.forEach((cuad, ci) => {
        const vars = micmac.variables.filter(v => v.cuadrante === cuad).slice(0, 3);
        if (vars.length === 0) return;
        y = checkOverflow(y, 20);
        drawRect(doc, margin, y, 3, vars.length * 9 + 4, cColors[ci]);
        setFont(doc, 7, cColors[ci], true);
        doc.text(cuad.toUpperCase(), margin + 5, y + 5);
        setFont(doc, 7, C.text);
        vars.forEach((v, vi) => {
          doc.text(`• ${(v.titulo || "").substring(0, 70)}`, margin + 5, y + 11 + vi * 8);
        });
        y += vars.length * 9 + 8;
      });
    }

    y = checkOverflow(y, 30);
    y = sectionTitle("Recomendación estratégica", y);
    setFont(doc, 8, C.text);
    const micLines = splitText(doc, micmac.narrativa.replace(/[*#]/g, ""), contentW - 4);
    doc.text(micLines.slice(0, 20), margin + 2, y);
  }

  // ── PÁG 4: IMPACTO FINANCIERO ─────────────────────────────────────────
  y = nuevaPagina("Impacto financiero");

  if (impacto) {
    y = sectionTitle("Mercado potencial", y);
    const fin = impacto;
    // Usar valores pasados directamente como parametros
    const tamVal = tam || fin.tam || fin.tam_millones || "—";
    const samVal = sam || fin.sam || fin.sam_millones || "—";
    const somVal = som || fin.som || fin.som_millones || "—";
    const cagr = fin.cagr || fin.CAGR || "14.5";
    const finKpis = [
      ["TAM", `USD ${tamVal}M`, "Mercado total"],
      ["SAM", `USD ${samVal}M`, "Mercado servible"],
      ["SOM", `USD ${somVal}M`, "Mercado alcanzable"],
      ["CAGR", `${cagr}%`, "Crecimiento anual"],
    ];

    // Escenarios financieros
    if (fin.escenarios?.length > 0) {
      y = sectionTitle("Oportunidad por escenario", y);
      fin.escenarios.slice(0, 4).forEach((esc, i) => {
        y = checkOverflow(y, 20);
        const opp = esc.oportunidad_usd
          ? `USD ${(esc.oportunidad_usd / 1e6).toFixed(1)}M`
          : "—";
        drawRect(doc, margin, y, contentW, 16, i % 2 === 0 ? "#f8fafb" : "#ffffff");
        setFont(doc, 9, C.navy, true);
        doc.text(String(esc.cluster || `Esc. ${i + 1}`), margin + 3, y + 6);
        setFont(doc, 8, C.muted);
        const narr = String(esc.narrativa || "").substring(0, 80);
        doc.text(narr, margin + 3, y + 12);
        setFont(doc, 10, C.emerald, true);
        doc.text(opp, W - margin - 3, y + 9, { align: "right" });
        y += 17;
      });
    }

    // Monte Carlo
    if (montecarlo?.length > 0) {
      y = checkOverflow(y, 30);
      y = sectionTitle("Simulación Monte Carlo", y);
      montecarlo.slice(0, 4).forEach((mc, i) => {
        y = checkOverflow(y, 14);
        const viable = Math.round((mc.prob_viable || 0) * 100);
        const viableColor = viable >= 80 ? C.emerald : viable >= 50 ? C.warn : C.danger;
        drawRect(doc, margin, y, contentW, 12, i % 2 === 0 ? "#f8fafb" : "#ffffff");
        setFont(doc, 8, C.navy, true);
        doc.text(String(mc.cluster || `Esc. ${i + 1}`), margin + 3, y + 7);
        setFont(doc, 7, C.muted);
        const p50 = mc.p50 ? `Mediana: USD ${(mc.p50 / 1e6).toFixed(1)}M` : "";
        const ic = (mc.p10 && mc.p90)
          ? `IC 80%: USD ${(mc.p10 / 1e6).toFixed(1)}M – USD ${(mc.p90 / 1e6).toFixed(1)}M`
          : "";
        doc.text(`${p50}  ${ic}`, margin + 25, y + 7);
        if (viable) {
          setFont(doc, 8, viableColor, true);
          doc.text(`Viabilidad ${viable}%`, W - margin - 3, y + 7, { align: "right" });
        }
        y += 13;
      });
    }
  }

  // ── PÁG 5: SOSTENIBILIDAD ─────────────────────────────────────────────
  if (sostenibilidad) {
    y = nuevaPagina("Inteligencia Sostenible");
    y = sectionTitle(`Indicadores ambientales — ${sostenibilidad.departamento || departamento}`, y);

    const ind = sostenibilidad.indicadores || {};
    const indKpis = [
      ["Cobertura boscosa", `${ind.cobertura_boscosa || "—"}%`, ""],
      ["Índice hídrico", `${ind.indice_hidrico || "—"}/10`, ""],
      ["Biodiversidad", ind.biodiversidad || "—", ""],
      ["Temperatura", ind.temperatura ? `${ind.temperatura}°C` : "N/D", "NASA POWER 2025"],
      ["Precipitación", ind.precipitacion ? `${ind.precipitacion} mm/d` : "N/D", "NASA POWER 2025"],
      ["Humedad", ind.humedad ? `${ind.humedad}%` : "N/D", ""],
      ["Registros SiB", ind.n_registros_bio ? ind.n_registros_bio.toLocaleString("es-CO") : "—", "biodiversidad"],
      ["Riesgo deforest.", ind.riesgo_deforestacion || "—", ""],
    ];

    const iW = contentW / 4;
    indKpis.forEach(([label, value, sub], i) => {
      kpiBox(label, value, sub, margin + (i % 4) * iW, y + Math.floor(i / 4) * 26, iW);
    });
    y += 58;

    // Impacto por escenario
    if (sostenibilidad.escenarios_impacto?.length > 0) {
      y = sectionTitle("Impacto ambiental por escenario", y);
      const escW = contentW / sostenibilidad.escenarios_impacto.length - 2;

      sostenibilidad.escenarios_impacto.forEach((esc, i) => {
        const balColor = esc.balance === "Sostenible" ? C.emerald
          : esc.balance === "Moderado" ? C.warn : C.danger;
        const eX = margin + i * (escW + 2);
        drawRect(doc, eX, y, escW, 35, "#f8fafb");
        drawRect(doc, eX, y, escW, 5, balColor);
        setFont(doc, 7, "#ffffff", true);
        doc.text(String(esc.cluster || `Esc.${i + 1}`), eX + 2, y + 3.8);
        setFont(doc, 6.5, C.muted);
        doc.text(`Hidrica: ${esc.huella_hidrica}/10`, eX + 2, y + 12);
        doc.text(`CO2: ${esc.co2_index}/10`, eX + 2, y + 19);
        doc.text(`Bio: ${esc.riesgo_bio}`, eX + 2, y + 26);
        setFont(doc, 7, balColor, true);
        doc.text(esc.balance || "", eX + 2, y + 32);
      });
      y += 42;
    }

    // Análisis LLM sostenibilidad
    if (sostenibilidad.analisis) {
      y = checkOverflow(y, 40);
      y = sectionTitle("Análisis de impacto ambiental", y);
      setFont(doc, 8, C.text);
      const analLines = splitText(doc, sostenibilidad.analisis.replace(/[*#]/g, ""), contentW - 4);
      const maxLines = Math.min(analLines.length, 18);
      doc.text(analLines.slice(0, maxLines), margin + 2, y);
      y += maxLines * 4.5 + 6;
    }
  }

  // ── PÁG 6: PLAN ESTRATÉGICO ───────────────────────────────────────────
  if (plan?.executive) {
    y = nuevaPagina("Plan Estrategico Integrado");
    y = sectionTitle("Plan Estrategico Integrado", y);

    const planText = plan.executive.replace(/[*#▪]/g, "").replace(/\n\n+/g, "\n");
    const planLines = splitText(doc, planText, contentW - 6);
    let lineCount = 0;

    for (const line of planLines) {
      y = checkOverflow(y, 6);
      const trimmed = line.trim();
      if (!trimmed) { y += 2; continue; }

      // Titulo de seccion numerada
      if (/^\d+\./.test(trimmed)) {
        y += 3;
        setFont(doc, 9, C.navy, true);
        const titleWrapped = splitText(doc, trimmed, contentW - 6);
        doc.text(titleWrapped, margin + 2, y);
        setFont(doc, 8, C.text);
        y += titleWrapped.length * 5 + 2;
      }
      // Bullet o item de lista
      else if (/^[-•*]/.test(trimmed) || /^\d+[\.\)]/.test(trimmed)) {
        setFont(doc, 8, C.text);
        const bulletWrapped = splitText(doc, trimmed, contentW - 10);
        doc.text(bulletWrapped, margin + 5, y);
        y += bulletWrapped.length * 4.5 + 1;
      }
      // Texto normal
      else {
        setFont(doc, 8, C.text);
        const wrappedLine = splitText(doc, trimmed, contentW - 6);
        doc.text(wrappedLine, margin + 2, y);
        y += wrappedLine.length * 4.5;
      }

      lineCount++;
      if (lineCount > 200) break;
    }
  }

  // ── PÁG CANVAS: MODELOS DE NEGOCIO ──────────────────────────────────
  if (canvasData.length > 0) {
    y = nuevaPagina("Modelos de Negocio por Escenario");
    y = sectionTitle("Business Model Canvas por Escenario", y);

    const labels = [
      { key: "propuesta_valor",   label: "PROPUESTA DE VALOR",  color: C.emerald },
      { key: "segmento_cliente",  label: "SEGMENTO CLIENTE",    color: C.slate },
      { key: "canales",           label: "CANALES",             color: C.slate },
      { key: "fuentes_ingreso",   label: "FUENTES DE INGRESO",  color: C.emerald },
      { key: "recursos_clave",    label: "RECURSOS CLAVE",      color: C.muted },
      { key: "actividades_clave", label: "ACTIVIDADES CLAVE",   color: C.muted },
      { key: "socios_clave",      label: "SOCIOS CLAVE",        color: C.muted },
      { key: "estructura_costos", label: "ESTRUCTURA COSTOS",   color: C.danger },
      { key: "impacto_ambiental", label: "IMPACTO AMBIENTAL",   color: C.emerald },
    ];

    canvasData.forEach((esc, ci) => {
      y = checkOverflow(y, 80);

      // Canvas header
      drawRect(doc, margin, y, contentW, 7, C.navy);
      setFont(doc, 9, "#ffffff", true);
      doc.text(String(esc.escenario || `Escenario ${ci + 1}`), margin + 4, y + 5);
      y += 9;

      // Canvas grid - 3 columns x 3 rows
      const colW = contentW / 3;
      const rowH = 20;

      labels.forEach((lbl, li) => {
        const col = li % 3;
        const row = Math.floor(li / 3);
        const cellX = margin + col * colW;
        const cellY = y + row * (rowH + 1);

        // Cell background
        drawRect(doc, cellX, cellY, colW - 1, rowH, "#f8fafb");
        drawRect(doc, cellX, cellY, colW - 1, rowH, C.border, false);

        // Label
        const [lr, lg, lb] = hexToRgb(lbl.color);
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(lr, lg, lb);
        doc.text(lbl.label, cellX + 3, cellY + 5);

        // Value
        setFont(doc, 7, C.text);
        const val = String(esc[lbl.key] || "—");
        const valLines = splitText(doc, val, colW - 6);
        doc.text(valLines.slice(0, 2), cellX + 3, cellY + 10);
      });

      y += 3 * (rowH + 1) + 8;
    });
  }

  // ── GUARDAR ───────────────────────────────────────────────────────────
  const filename = `BioScope_${(sector || "Analisis").replace(/\s/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}

// ── Botón de exportación ──────────────────────────────────────────────────
export default function ExportarPDF({ datos, disabled }) {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (disabled || loading) return;
    setLoading(true);
    try {
      await generarPDF(datos);
    } catch (e) {
      console.error("Error generando PDF:", e);
    }
    setLoading(false);
  };

  return (
    <button
      onClick={handleExport}
      disabled={disabled || loading}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "8px 16px", borderRadius: 8,
        background: disabled ? "#e2e5e4" : C.navy,
        color: disabled ? "#8E9A98" : "#ffffff",
        border: "none", cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 12, fontWeight: 600,
        transition: "opacity 0.2s",
        opacity: loading ? 0.7 : 1,
      }}
    >
      {loading ? "⏳ Generando PDF..." : "📄 Exportar informe"}
    </button>
  );
}
